import Decimal from "decimal.js";

/**
 * Whether we are willing to show a number for a currency, and why.
 *
 * Derived from what the providers actually returned — never hardcoded — so a
 * currency's verdict changes when the world does rather than when someone
 * remembers to edit a list.
 *
 * THE PRINCIPLE: no rate is better than a wrong rate. But "wrong" has to be
 * earned. An official rate that differs from the street is not wrong, it is
 * missing context, and the cure for missing context is context. So there are
 * three states rather than two — see CLAUDE.md for the measurement behind the
 * thresholds.
 */

export type CoverageState = "ok" | "caveated" | "withheld";

export type CoverageReason =
  /** Providers agree. Nothing to say. */
  | "agree"
  /** Providers differ enough that an official rate and a street rate diverge. */
  | "market-divergence"
  /** Exactly one provider quotes it, so nothing can be checked against it. */
  | "single-source"
  /** No provider carries it at all. */
  | "no-source"
  /** The freshest rate we have is past its staleness window. */
  | "stale"
  /** Providers disagree so far apart that they are quoting different units. */
  | "sources-disagree"
  /** Demonetised. A live quote for it would be meaningless. */
  | "withdrawn";

export interface Observation {
  readonly provider: string;
  /** Units of this currency per 1 USD, as a string to avoid float loss. */
  readonly rate: string;
  /** ISO date the provider quoted it. */
  readonly date: string;
}

export interface Coverage {
  readonly state: CoverageState;
  readonly reason: CoverageReason;
  /** The figure to display, or null when withheld. */
  readonly rate: string | null;
  readonly asOf: string | null;
  /** Relative spread across providers, as a percentage. Null below two. */
  readonly spreadPct: number | null;
  /** For `withdrawn`: what replaced it. */
  readonly replacedBy?: string;
}

/* ---- thresholds. The reasoning lives in CLAUDE.md. ---- */

/** Below this, providers are merely rounding and timing differently. */
export const AGREE_PCT = 2;
/**
 * Above this, providers are not disagreeing about a price — they are quoting
 * different units. Measured: the largest genuine market disagreement is 10.5%,
 * the next value up is 10,577%, and nothing sits between 25% and 900%.
 */
export const DISAGREE_PCT = 50;

/** A weekend plus a public holiday, and no more. */
export const STALE_DAYS_FLOATING = 7;
/** A peg does not move, so its rate survives longer — but not forever. */
export const STALE_DAYS_PEGGED = 30;
/** Past this drift, a declared peg is not being honoured and we stop trusting it. */
export const PEG_BREAK_PCT = 1;

export interface DeriveOptions {
  readonly now: Date;
  /** Declared peg against USD, if any. Self-checked below. */
  readonly peg?: { readonly to: "USD"; readonly rate: string };
  /** Set for demonetised codes. */
  readonly withdrawn?: { readonly replacedBy: string };
}

const DAY_MS = 86_400_000;

function ageInDays(date: string, now: Date): number {
  const t = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return Math.floor((now.getTime() - t) / DAY_MS);
}

/**
 * The median, not the mean. One provider quoting a redenominated unit would
 * drag a mean by orders of magnitude; the median simply ignores it.
 */
function median(values: readonly Decimal[]): Decimal {
  const sorted = [...values].sort((a, b) => a.comparedTo(b));
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] as Decimal;
  const lo = sorted[mid - 1] as Decimal;
  const hi = sorted[mid] as Decimal;
  return lo.plus(hi).div(2);
}

export function deriveCoverage(
  observations: readonly Observation[],
  options: DeriveOptions,
): Coverage {
  if (options.withdrawn) {
    return {
      state: "withheld",
      reason: "withdrawn",
      rate: null,
      asOf: null,
      spreadPct: null,
      replacedBy: options.withdrawn.replacedBy,
    };
  }

  const usable = observations.filter((o) => {
    try {
      return new Decimal(o.rate).greaterThan(0);
    } catch {
      return false;
    }
  });

  if (usable.length === 0) {
    return { state: "withheld", reason: "no-source", rate: null, asOf: null, spreadPct: null };
  }

  const rates = usable.map((o) => new Decimal(o.rate));
  const chosen = median(rates);
  /** The freshest date across providers: staleness is about the best we have. */
  const asOf = usable.map((o) => o.date).sort().at(-1) ?? null;

  /*
   * A declared peg is trusted only while the data agrees with it. Egypt broke
   * its peg three times in eight years; a hardcoded list would have gone on
   * granting a 30-day window to a currency that had started floating.
   */
  let pegged = false;
  if (options.peg) {
    const declared = new Decimal(options.peg.rate);
    const drift = chosen.minus(declared).abs().div(declared).times(100);
    pegged = drift.lessThanOrEqualTo(PEG_BREAK_PCT);
  }

  const limit = pegged ? STALE_DAYS_PEGGED : STALE_DAYS_FLOATING;
  if (asOf !== null && ageInDays(asOf, options.now) > limit) {
    return { state: "withheld", reason: "stale", rate: null, asOf, spreadPct: null };
  }

  if (usable.length === 1) {
    return {
      state: "caveated",
      reason: "single-source",
      rate: chosen.toString(),
      asOf,
      spreadPct: null,
    };
  }

  const min = rates.reduce((a, b) => (a.lessThan(b) ? a : b));
  const max = rates.reduce((a, b) => (a.greaterThan(b) ? a : b));
  const spreadPct = max.minus(min).div(min).times(100).toNumber();

  if (spreadPct > DISAGREE_PCT) {
    return { state: "withheld", reason: "sources-disagree", rate: null, asOf, spreadPct };
  }
  if (spreadPct > AGREE_PCT) {
    return {
      state: "caveated",
      reason: "market-divergence",
      rate: chosen.toString(),
      asOf,
      spreadPct,
    };
  }
  return { state: "ok", reason: "agree", rate: chosen.toString(), asOf, spreadPct };
}
